import { RequireAccess } from '@/components/require-access'
import { calculateCampaignDurationInDays, centsToEuros } from '@/constants'
import { CampaignModel } from '@/ui'
import { BudgetIcon, CalendarIcon, LinkIcon } from '@/ui'
import { WidgetInputToggleField } from '@/ui/rapid-ui/inputs/widgets/widget-input-toggle-field'
import { CustomFieldWithValueData } from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { format } from 'date-fns'
import { GlobeIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { WhatsAppAutomationOverride } from './whatsapp-automation-override'

export type SettingsPanelProps = {
	campaign: CampaignModel
	reachOverride?: { url: string }
	isLoading: boolean
	careerSite?: {
		visible: boolean
		onChange: (visible: boolean) => void
		disabled: boolean
		isOnlyChannel: boolean
		hasError: boolean
	}
}

export function SettingsPanel({ campaign, reachOverride, isLoading, careerSite }: SettingsPanelProps) {
	const { t } = useTranslation('common')

	const durationDays = campaign.startedAt && campaign.endsAt
		? calculateCampaignDurationInDays(campaign.startedAt, campaign.endsAt)
		: 0

	const formatDate = (date: Date) => format(date, 'dd.MM.yyyy')

	const targetAreas = campaign.adCampaignLocations.length > 1
		? campaign.adCampaignLocations
		: campaign.adCampaignLocations.filter((area) => area.name !== campaign.location)

	return (
		<div className="space-y-4 mt-2">
			{/* Header */}
			<div className="flex items-center justify-between">
				<h2 className="text-xl font-semibold text-grey-900">{t('overview.settingsTitle')}</h2>
				<span className="text-grey-500 text-sm font-mono">
					ID: {campaign.id.substring(0, 8)}
				</span>
			</div>

			{/* Settings Cards */}
			<div className="grid gap-4 p-4 bg-white rounded-xl border border-grey-100">
				{/* Budget Card */}
				{!campaign.isEvergreen && (
					<RequireAccess permission="campaign:view-budget">
						<SettingCard
							icon={<BudgetIcon />}
							iconBgColor="bg-purple-100"
							title={t('overview.budget', { amount: centsToEuros(campaign.budget) })}
							subtitle={t('overview.durationDays', { days: durationDays })}
							isLoading={isLoading}
						/>
					</RequireAccess>
				)}

				{/* Addon Cards */}
				<RequireAccess permission="addons:show">
					<>
						{campaign.addons.map((addon) => (
							<SettingCard
								key={addon.id}
								icon={<BudgetIcon />}
								iconBgColor="bg-purple-100"
								title={addon.name}
								subtitle={`${centsToEuros(addon.price!)} €`}
								isLoading={isLoading}
							/>
						))}
					</>
				</RequireAccess>

				<SettingsList
					title={t('settings.customFields.title')}
					items={campaign.customFields.map((field: CustomFieldWithValueData) => ({
						key: field.id,
						label: field.name,
						value: field.value,
					}))}
					isLoading={isLoading}
				/>

				{/* Dates Card */}
				{!campaign.isEvergreen && (
					<SettingCard
						icon={<CalendarIcon />}
						iconBgColor="bg-purple-100"
						title={t('overview.starts', { date: campaign.startedAt ? formatDate(campaign.startedAt) : '' })}
						subtitle={t('overview.ends', { date: campaign.endsAt ? formatDate(campaign.endsAt) : '' })}
						isLoading={isLoading}
					/>
				)}

				{!campaign.isEvergreen && targetAreas.length > 0 && (
					<SettingCard
						icon={<GlobeIcon className="size-5" />}
						iconBgColor="bg-purple-100"
						title={t('overview.targetArea')}
						subtitle={targetAreas.map((area) => area.name).join(', ')}
						isLoading={isLoading}
					/>
				)}

				<WhatsAppAutomationOverride
					campaignId={campaign.id}
					organisationId={campaign.organisationId}
					value={campaign.firstContactAutomationEnabled}
					scoreThreshold={campaign.firstContactScoreThreshold}
				/>

				{reachOverride && (
					<a
						href={reachOverride.url}
						target="_blank"
						rel="noopener noreferrer"
						className="hover:opacity-80 transition-opacity"
					>
						<SettingCard
							icon={<LinkIcon className="w-8 h-8" />}
							iconBgColor="bg-purple-100"
							title={t('overview.customLandingPage')}
							subtitle={reachOverride.url}
							isLoading={isLoading}
						/>
					</a>
				)}

				{careerSite && (
					<SettingCard
						icon={<GlobeIcon className="size-5" />}
						iconBgColor="bg-purple-100"
						title={t('overview.careerSite')}
						subtitle={careerSite.visible
							? t(
								careerSite.isOnlyChannel
									? 'overview.careerSiteOnlyChannel'
									: 'overview.careerSiteVisible',
							)
							: t('overview.careerSiteHidden')}
						error={careerSite.hasError ? t('overview.careerSiteUpdateFailed') : undefined}
						isLoading={isLoading}
						action={
							<WidgetInputToggleField
								value={careerSite.visible}
								onChange={careerSite.onChange}
								disabled={careerSite.disabled}
								ariaLabel="Toggle career site visibility"
							/>
						}
					/>
				)}
			</div>
		</div>
	)
}

type SettingCardProps = {
	icon: React.ReactNode
	iconBgColor: string
	title: string
	subtitle: string
	error?: string
	action?: React.ReactNode
	isLoading?: boolean
}

function SettingCard({ icon, iconBgColor, title, subtitle, error, action, isLoading }: SettingCardProps) {
	return (
		<div className="flex items-center gap-4">
			<div className={ctl(`${iconBgColor} p-3 rounded-lg text-purple-600`)}>
				{icon}
			</div>
			<div className="flex-1 min-w-0">
				{isLoading
					? (
						<>
							<div className="h-4 w-32 bg-grey-200 rounded animate-pulse mb-1" />
							<div className="h-3 w-24 bg-grey-100 rounded animate-pulse" />
						</>
					)
					: (
						<>
							<div className="font-medium text-grey-900">{title}</div>
							<div className="text-grey-600 text-sm truncate">{subtitle}</div>
							{error ? <div className="text-red-600 text-sm">{error}</div> : null}
						</>
					)}
			</div>
			{action && <div>{action}</div>}
		</div>
	)
}

type SettingsListProps = {
	title: string
	items: { key: string; label: string; value: string }[]
	isLoading?: boolean
}

function SettingsList({ title, items, isLoading }: SettingsListProps) {
	if (items.length === 0) { return null }

	return (
		<div className="flex gap-4">
			<div className="w-14 flex-shrink-0" />
			<div className="flex-1 min-w-0">
				{isLoading
					? (
						<>
							<div className="h-4 w-32 bg-grey-200 rounded animate-pulse mb-1" />
							<div className="h-3 w-24 bg-grey-100 rounded animate-pulse" />
						</>
					)
					: (
						<>
							<div className="font-medium text-grey-900">{title}</div>
							<div className="space-y-1 mt-1">
								{items.map((item) => (
									<div key={item.key} className="flex justify-between items-center">
										<span className="text-grey-600 text-sm">{item.label}</span>
										<span className="text-grey-600 text-sm">{item.value}</span>
									</div>
								))}
							</div>
						</>
					)}
			</div>
		</div>
	)
}
